import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
    faArrowRightToBracket,
    faBuilding,
    faHandHoldingBox,
    faUser,
    faWarehouseFull,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useBreadcrumbs from 'use-react-router-breadcrumbs';

import { getEnvironmentVariable } from '@spacefill/shared/src/utils/GetEnvVar';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { SpacefillDebugActions } from '@spacefill/uikit/src/SpacefillDebugActions';
import { Breadcrumb } from '@spacefill/uikit/src/components/Breadcrumb';

import { TopBarActionsAndNotifications } from './TopBarActionsAndNotifications';
import { TopBarProfile } from './TopBarProfile';
import { routes } from './TopbarRoutes';

const SpacefillTopbar = () => {
    const breadcrumbs = useBreadcrumbs(routes);
    const { user, switchRole } = useUserContext();
    const { t } = useTranslation();
    const location = useLocation();
    const isCarrier = !!user.carrierId;

    const profileTopNavigation = user.multiRoles
        ? [
              {
                  title: t('Operator view'),
                  hasRoundedIcon: true,
                  icon: <FontAwesomeIcon icon={faWarehouseFull} />,
                  disabled: user.role === 'PROVIDER',
                  dataTestid: `nav-switch-operator${user.role === 'PROVIDER' ? '-disabled' : ''}`,
                  onClick: async () => await switchRole('PROVIDER'),
                  conditionForDisplay: () => !isCarrier,
              },
              {
                  title: t('Shipper view'),
                  hasRoundedIcon: true,
                  icon: <FontAwesomeIcon icon={faHandHoldingBox} />,
                  disabled: user.role === 'SHIPPER',
                  dataTestid: `nav-switch-shipper${user.role === 'SHIPPER' ? '-disabled' : ''}`,
                  onClick: async () => await switchRole('SHIPPER'),
                  conditionForDisplay: () => !isCarrier,
              },
          ]
        : [];
    const profileNavigation = [
        {
            title: t('Company profile'),
            hasRoundedIcon: true,
            icon: <FontAwesomeIcon icon={faBuilding} />,
            dataTestid: 'nav-company',
            isActive: location.pathname.startsWith('/company-profile/'),
            to: '/company-profile/',
            conditionForDisplay: () => !isCarrier,
        },
        {
            title: t('User profile'),
            hasRoundedIcon: true,
            icon: <FontAwesomeIcon icon={faUser} />,
            dataTestid: 'nav-profile',
            isActive: location.pathname.startsWith('/profile/'),
            to: '/profile/',
        },
        {
            title: t('Log out'),
            hasRoundedIcon: true,
            dataTestId: 'nav-logout',
            icon: <FontAwesomeIcon icon={faArrowRightToBracket} />,
            to: '/logout/',
        },
    ];
    return (
        <div className='flex items-center justify-between bg-gray-50 border-b border-gray-200 px-6 py-2 h-12'>
            <Breadcrumb breadcrumbs={breadcrumbs} />
            <div className='flex items-center gap-4'>
                {(user.isStaff || getEnvironmentVariable('featureTogglingEnabled')) && <SpacefillDebugActions />}
                <TopBarActionsAndNotifications />
                <TopBarProfile
                    profileTopNavigation={profileTopNavigation}
                    profileNavigation={profileNavigation}
                    userInfo={{
                        userName: `${user?.firstName} ${user?.lastName}`,
                        company: user?.customer?.company || user?.logisticProvider?.company || user?.carrier?.company,
                    }}
                />
            </div>
        </div>
    );
};

export { SpacefillTopbar };
